import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Tooltip,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  CircularProgress,
  Stack,
  Avatar,
  Grid,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../services/useApi';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
} from '../styles/theme';

interface GatewayNfcom {
  id: number;
  titulo: string;
  tipo: 'nuvemfiscal' | 'vigonfcom' | 'sefazrs';
  ambiente: 'producao' | 'homologacao';
  ambiente_sefaz: 'producao' | 'homologacao';
  is_default: boolean;
  client_id: string;
  client_secret: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

const GatewaysNfcomPage: React.FC = () => {
  const theme = useTheme();
  const [gateways, setGateways] = useState<GatewayNfcom[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGateway, setEditingGateway] = useState<GatewayNfcom | null>(null);
  const [formData, setFormData] = useState({
    titulo: '',
    tipo: 'nuvemfiscal' as 'nuvemfiscal' | 'vigonfcom' | 'sefazrs',
    ambiente: 'homologacao' as 'producao' | 'homologacao',
    ambiente_sefaz: 'homologacao' as 'producao' | 'homologacao',
    client_id: '',
    client_secret: '',
    ativo: true,
    is_default: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });


  useEffect(() => {
    carregarGateways();
  }, []);

  const carregarGateways = async () => {
    try {
      setLoading(true);
      const response = await api.get('/gateways-nfcom');
      setGateways(response.data);
    } catch (error) {
      console.error('Erro ao carregar gateways:', error);
      mostrarSnackbar('Erro ao carregar gateways', 'error');
    } finally {
      setLoading(false);
    }
  };

  const mostrarSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (gateway?: GatewayNfcom) => {
    if (gateway) {
      setEditingGateway(gateway);
      setFormData({
        titulo: gateway.titulo,
        tipo: gateway.tipo,
        ambiente: gateway.ambiente,
        ambiente_sefaz: gateway.ambiente_sefaz,
        client_id: gateway.client_id,
        client_secret: gateway.client_secret,
        ativo: gateway.ativo,
        is_default: gateway.is_default,
      });
    } else {
      setEditingGateway(null);
      setFormData({
        titulo: '',
        tipo: 'nuvemfiscal',
        ambiente: 'homologacao',
        ambiente_sefaz: 'homologacao',
        client_id: '',
        client_secret: '',
        ativo: true,
        is_default: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGateway(null);
    setFormData({
      titulo: '',
      tipo: 'nuvemfiscal',
      ambiente: 'homologacao',
      ambiente_sefaz: 'homologacao',
      client_id: '',
      client_secret: '',
      ativo: true,
      is_default: false,
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.titulo || !formData.tipo) {
        mostrarSnackbar('Preencha todos os campos obrigatórios', 'error');
        return;
      }

      // Para gateways que não são SEFAZ RS, client_id e client_secret são obrigatórios
      if (formData.tipo !== 'sefazrs' && (!formData.client_id || !formData.client_secret)) {
        mostrarSnackbar('Preencha Client ID e Client Secret', 'error');
        return;
      }

      // Para SEFAZ RS, preencher client_id e client_secret com valores padrão se vazios
      const dadosEnvio = {
        ...formData,
        client_id: formData.client_id || (formData.tipo === 'sefazrs' ? 'sefazrs-direto' : formData.client_id),
        client_secret: formData.client_secret || (formData.tipo === 'sefazrs' ? 'certificado-digital' : formData.client_secret),
      };

      if (editingGateway) {
        await api.put(`/gateways-nfcom/${editingGateway.id}`, dadosEnvio);
        mostrarSnackbar('Gateway atualizado com sucesso', 'success');
      } else {
        await api.post('/gateways-nfcom', dadosEnvio);
        mostrarSnackbar('Gateway criado com sucesso', 'success');
      }

      handleCloseDialog();
      carregarGateways();
    } catch (error) {
      console.error('Erro ao salvar gateway:', error);
      mostrarSnackbar('Erro ao salvar gateway', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja deletar este gateway?')) {
      try {
        await api.delete(`/gateways-nfcom/${id}`);
        mostrarSnackbar('Gateway deletado com sucesso', 'success');
        carregarGateways();
      } catch (error) {
        console.error('Erro ao deletar gateway:', error);
        mostrarSnackbar('Erro ao deletar gateway', 'error');
      }
    }
  };

  const handleToggleAtivo = async (id: number, ativo: boolean) => {
    try {
      await api.put(`/gateways-nfcom/${id}`, { ativo: !ativo });
      mostrarSnackbar(`Gateway ${!ativo ? 'ativado' : 'desativado'} com sucesso`, 'success');
      carregarGateways();
    } catch (error) {
      console.error('Erro ao alterar status do gateway:', error);
      mostrarSnackbar('Erro ao alterar status do gateway', 'error');
    }
  };

  const handleDefinirComoPadrao = async (id: number) => {
    try {
      await api.patch(`/gateways-nfcom/${id}/padrao`);
      mostrarSnackbar('Gateway definido como padrão com sucesso', 'success');
      carregarGateways();
    } catch (error) {
      console.error('Erro ao definir gateway como padrão:', error);
      mostrarSnackbar('Erro ao definir gateway como padrão', 'error');
    }
  };

  return (
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'secondary')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <ReceiptIcon sx={{ fontSize: 120, color: theme.palette.secondary.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}` }}>
              <ReceiptIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gateways NFCom</Typography>
              <Typography variant="caption" color="text.secondary">Integrações para emissão de notas fiscais de comunicação</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={carregarGateways}>Atualizar</Button>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Novo Gateway</Button>
          </Stack>
        </Stack>
      </Paper>

      {!loading && gateways.length > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Total" valor={gateways.length} icone={<ReceiptIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Ativos" valor={gateways.filter(g => g.ativo).length} icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Produção" valor={gateways.filter(g => g.ambiente === 'producao').length} icone={<StarIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Padrão" valor={gateways.filter(g => g.is_default).length} icone={<StarBorderIcon sx={{ color: theme.palette.warning.main }} />} cor={theme.palette.warning.main} bgCor={alpha(theme.palette.warning.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Gateways NFCom</Typography>
          <Chip size="small" label={`${gateways.length} gateway${gateways.length !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        </Box>

        {loading ? (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando gateways...</Typography>
          </Box>
        ) : gateways.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhum gateway NFCom cadastrado</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={rankingTableHeadRowSx(theme)}>
                  <TableCell>Título</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Ambiente</TableCell>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Padrão</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gateways.map((gateway) => (
                  <TableRow key={gateway.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                    <TableCell>{gateway.titulo}</TableCell>
                    <TableCell>
                      <Chip
                        label={gateway.tipo === 'nuvemfiscal' ? 'Nuvem Fiscal' : gateway.tipo === 'sefazrs' ? 'SEFAZ RS' : 'Vigo NFCom'}
                        color={gateway.tipo === 'nuvemfiscal' ? 'primary' : gateway.tipo === 'sefazrs' ? 'info' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={gateway.ambiente === 'producao' ? 'Produção' : 'Homologação'}
                        color={gateway.ambiente === 'producao' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{gateway.client_id}</TableCell>
                    <TableCell>
                      <Chip
                        icon={gateway.ativo ? <CheckCircleIcon /> : <CancelIcon />}
                        label={gateway.ativo ? 'Ativo' : 'Inativo'}
                        color={gateway.ativo ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {gateway.is_default ? (
                        <Chip
                          icon={<StarIcon />}
                          label="Padrão"
                          color="primary"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<StarBorderIcon />}
                          label="Não padrão"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(gateway.createdAt).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(gateway)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={gateway.ativo ? 'Desativar' : 'Ativar'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleAtivo(gateway.id, gateway.ativo)}
                        >
                          {gateway.ativo ? <CancelIcon /> : <CheckCircleIcon />}
                        </IconButton>
                      </Tooltip>
                      {!gateway.is_default && (
                        <Tooltip title="Definir como padrão">
                          <IconButton
                            size="small"
                            onClick={() => handleDefinirComoPadrao(gateway.id)}
                          >
                            <StarIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Deletar">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(gateway.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingGateway ? 'Editar Gateway' : 'Novo Gateway'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Título"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'nuvemfiscal' | 'vigonfcom' | 'sefazrs' })}
                label="Tipo"
              >
                <MenuItem value="nuvemfiscal">Nuvem Fiscal</MenuItem>
                <MenuItem value="sefazrs">SEFAZ RS (Direto)</MenuItem>
                <MenuItem value="vigonfcom">Vigo NFCom</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Ambiente</InputLabel>
              <Select
                value={formData.ambiente}
                onChange={(e) => setFormData({ ...formData, ambiente: e.target.value as 'producao' | 'homologacao' })}
                label="Ambiente"
              >
                <MenuItem value="homologacao">Homologação</MenuItem>
                <MenuItem value="producao">Produção</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Ambiente Sefaz</InputLabel>
              <Select
                value={formData.ambiente_sefaz}
                onChange={(e) => setFormData({ ...formData, ambiente_sefaz: e.target.value as 'producao' | 'homologacao' })}
                label="Ambiente Sefaz"
              >
                <MenuItem value="homologacao">Homologação</MenuItem>
                <MenuItem value="producao">Produção</MenuItem>
              </Select>
            </FormControl>
            {formData.tipo === 'sefazrs' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Na comunicação direta com a SEFAZ RS, o certificado digital configurado no escritório será utilizado para assinar o XML e autenticar na SEFAZ. Os campos Client ID e Client Secret abaixo são opcionais (podem ser preenchidos com qualquer valor como identificação interna).
              </Alert>
            )}
            <TextField
              label={formData.tipo === 'sefazrs' ? 'Identificador (opcional)' : 'Client ID'}
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              fullWidth
              required={formData.tipo !== 'sefazrs'}
              helperText={formData.tipo === 'sefazrs' ? 'Opcional - usado apenas como identificação interna' : ''}
            />
            <TextField
              label={formData.tipo === 'sefazrs' ? 'Descrição (opcional)' : 'Client Secret'}
              value={formData.client_secret}
              onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
              fullWidth
              required={formData.tipo !== 'sefazrs'}
              type={formData.tipo === 'sefazrs' ? 'text' : 'password'}
              helperText={formData.tipo === 'sefazrs' ? 'Opcional - usado apenas como identificação interna' : ''}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                />
              }
              label="Ativo"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                />
              }
              label="Gateway Padrão"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingGateway ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GatewaysNfcomPage;
